/**
 * Type guard that narrows out `null` and `undefined`.
 * Handy as a `.filter(isDefined)` callback.
 */
export function isDefined<TValue>(
    value: TValue | null | undefined
): value is TValue {
    return value !== null && value !== undefined;
}

/**
 * True when the array is undefined or has no items.
 * For strings use `isEmpty` from `@realwork/crux-utils/string`.
 */
export function isEmpty<T>(array: T[] | undefined): boolean {
    return !isNotEmpty(array);
}

/** True when the array is defined and has at least one item. */
export function isNotEmpty<T>(array: T[] | undefined): boolean {
    return !!array?.length;
}

/**
 * Returns the items at even indexes (0, 2, 4, ...) when `isEven` is true,
 * otherwise the items at odd indexes. Empty or missing input yields `[]`.
 */
export function everyOtherItem<T>(array: T[], isEven: boolean): T[] {
    if (!array || !array.length) {
        return [];
    }
    return array.filter((item, index) => {
        if (!isEven) {
            return index % 2 !== 0;
        }
        return index % 2 === 0;
    });
}

/** Splits `array` into consecutive slices of at most `chunkSize` items. */
export function chunk<T>(array: T[], chunkSize: number): T[][] {
    const results: T[][] = [];
    for (let i = 0; i < array.length; i += chunkSize) {
        const chunk = array.slice(i, i + chunkSize);
        results.push(chunk);
    }
    return results;
}

/** Flattens one level of nesting; `null`/`undefined` inner arrays are skipped. */
export function flatten2DArray<T>(array: T[][]): T[] {
    return array.reduce(
        (result, currArray) => result.concat(currArray || []),
        [] as T[]
    );
}

export type IntersectionComparator<T> = (left: T, right: T) => boolean;

/**
 * Items of `left` that have a match in `right` according to `comparator`
 * (strict equality by default). Preserves the order of `left`.
 */
export function intersection<T>(
    left: T[],
    right: T[],
    comparator: IntersectionComparator<T> = defaultComparator
): T[] {
    return left.filter((l) => right.some((r) => comparator(l, r)));
}

function defaultComparator<T>(left: T, right: T): boolean {
    return left === right;
}
